import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate } from 'react-router-dom'

import { Button, H4, Icon, Intent, Section, SectionCard } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import me from '../../domain/api/me'
import profile from '../../domain/api/profile'
import ApiError from '../common/apiError/ApiError'
import Empty from '../common/empty/Empty'
import Mount from '../common/mount/Mount'
import PrimaryButton from '../common/primaryButton/PrimaryButton'
import ProfileDetail from '../profileDetail/ProfileDetail'
import { useRootOutletContext } from '../root/Root'
import styles from './Profiles.module.scss'
import ProfilesSkeleton from './ProfilesSkeleton'

export default (props: {
    redirectWhenNotAuthenticated: To,
    profileLink: (profileId: string) => To,
    cardLink: (cardId: string) => To,
}) => {
    const auth = useAuth()
    const { toaster } = useRootOutletContext()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const profilesResponse = me.profiles.useFetchSWR()
    const { data: profiles, mutate: mutateProfiles } = profilesResponse
    const { data: cards } = me.cards.useFetchSWR()
    const { trigger: insertProfile, isMutating: isInsertingProfile } = profile.useInsertSWR()

    const numberOfCards = cards?.length ?? 0
    const numberOfProfiles = profiles?.length ?? 9_999
    const isAddButtonDisabled = isInsertingProfile || numberOfProfiles >= numberOfCards + 4

    useEffect(() => {
        if (auth.isLoaded && !auth.isAuthenticated) navigate(props.redirectWhenNotAuthenticated)
    }, [auth, navigate, props])

    const addNew = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
        const result = await insertProfile()

        if (result.status !== 201)
            return toaster?.show({ message: t('toast.failedToAddNewProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })

        mutateProfiles(prev => [...(prev ?? []), result.data])
        toaster?.show({ message: t('toast.newProfileAdded.message'), icon: IconNames.Add, intent: Intent.SUCCESS })
    }, [insertProfile, mutateProfiles, toaster, t])

    const duplicate = useCallback((profileId: string) => async (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()

        const original = profiles?.find(profile => profile.id === profileId)
        if (original === undefined) return
        const result = await insertProfile(original)

        if (result.status !== 201)
            return toaster?.show({ message: t('toast.failedToDuplicateProfile.message'), icon: IconNames.Cross, intent: Intent.DANGER })

        mutateProfiles(prev => [...(prev ?? []), result.data])
        toaster?.show({ message: t('toast.profileDuplicate.message'), icon: IconNames.Duplicate, intent: Intent.SUCCESS })
    }, [insertProfile, mutateProfiles, profiles, toaster, t])

    const linkIcon = useCallback((profileId: string) => {
        const linkedCard = cards?.find(card => card.profile_id === profileId)
        return linkedCard
            ? <Icon icon={IconNames.Link} intent={Intent.PRIMARY} onClick={() => navigate(props.cardLink(linkedCard.id))} />
            : <Icon icon={IconNames.Unlink} intent={Intent.NONE} />
    }, [cards, navigate, props])

    return <>
        <H4 className={styles.title}>{t('profiles.title')}</H4>
        <div className={styles.verticalContainer}>
            <Mount response={profilesResponse}
                loading={() => <ProfilesSkeleton />}
                failure={error => <ApiError error={error} />}
                empty={() =>
                    <Empty
                        lottie='profile'
                        title={t('profiles.empty.label')}
                        description={t('profiles.empty.description')}
                        buttonProps={{
                            text: t('profiles.addNewButton.label'),
                            icon: IconNames.Add,
                            onClick: addNew,
                            disabled: isAddButtonDisabled,
                        }}
                    />}
                success={data => <>
                    {[...data] // sort mutates the original array and can cause an error
                        .sort((a, b) => a.created_at - b.created_at)
                        .map((profile, index) =>
                            <Section
                                icon={linkIcon(profile.id)}
                                key={`section-${index}`}
                                title={`#${(index + 1).toString().padStart(3, '0')}`}
                                subtitle={t('profiles.lastUpdated.label', {
                                    date: new Date(profile.updated_at * 1_000),
                                })}
                                rightElement={
                                    <Button
                                        text={t('profiles.detailButton.label')}
                                        icon={IconNames.DocumentOpen}
                                        onClick={() => navigate(props.profileLink(profile.id))}
                                        intent={Intent.PRIMARY}
                                        minimal />}
                                collapsible
                                collapseProps={{ defaultIsOpen: false, }}>
                                <SectionCard>
                                    <ProfileDetail data={profile} />
                                    <PrimaryButton
                                        text={t('profiles.duplicateButton.label')}
                                        icon={IconNames.Duplicate}
                                        onClick={duplicate(profile.id)}
                                        disabled={isAddButtonDisabled}
                                        alternative />
                                </SectionCard>
                            </Section>
                        )}
                    <PrimaryButton
                        text={t('profiles.addNewButton.label')}
                        icon={IconNames.Add}
                        onClick={addNew}
                        disabled={isAddButtonDisabled} />
                </>}
            />
        </div>
    </>
}